import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ArrowRight,
  ImageOff,
  Loader2,
  PackageOpen,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { qrPrintApi } from '../../../api/qrPrintApi';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  formatMoney,
} from '../../../utils/qrPrintHelpers';

import '../../../styles/QrPrint/QrPrintStaticRecommendationsModal.css';

const normalizeImageUrl = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    return value;
  }

  return (
    value.url ||
    value.image_url ||
    value.path ||
    value.src ||
    ''
  );
};

const getProductImage = (product) => {
  return (
    normalizeImageUrl(product?.main_image_url) ||
    normalizeImageUrl(product?.main_image) ||
    normalizeImageUrl(product?.image_url) ||
    normalizeImageUrl(product?.image) ||
    normalizeImageUrl(product?.gallery_images?.[0]) ||
    normalizeImageUrl(product?.images?.[0]) ||
    ''
  );
};

const optionLabel = (option) => {
  if (
    typeof option === 'string' ||
    typeof option === 'number'
  ) {
    return String(option);
  }

  return String(
    option?.label ??
      option?.name ??
      option?.value ??
      '',
  );
};

export default function QrPrintStaticRecommendationsModal({
  open,
  menu,
  establishmentId,
  onClose,
}) {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [products, setProducts] =
    useState([]);
  const [loading, setLoading] =
    useState(false);
  const [error, setError] =
    useState('');

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const response =
          await qrPrintApi.getProducts({
            active: 1,
            per_page: 50,
          });

        if (!active) {
          return;
        }

        const payload =
          response.data?.data;

        const list =
          Array.isArray(payload)
            ? payload
            : payload?.data ||
              response.data?.products ||
              [];

        setProducts(
          list.filter(
            (product) =>
              product?.is_active !== false,
          ),
        );
      } catch (requestError) {
        if (!active) {
          return;
        }

        setError(
          requestError?.response?.data
            ?.message ||
            t('qrPrint.static.errors.load'),
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [open]);

  const sortedProducts = useMemo(
    () =>
      [...products].sort(
        (a, b) =>
          Number(
            b?.is_recommended || 0,
          ) -
            Number(
              a?.is_recommended || 0,
            ) ||
          Number(a?.sort_order || 0) -
            Number(b?.sort_order || 0),
      ),
    [products],
  );

  if (!open) {
    return null;
  }

  const openProduct = (product) => {
    if (!product?.id || !menu?.id) {
      return;
    }

    const params =
      new URLSearchParams({
        menuId:
          String(menu.id),

        establishmentId:
          String(establishmentId),
      });

    /*
     * No customizationId is passed here.
     * The normal customizer therefore starts from the
     * product defaults configured by the Super Admin.
     */
    navigate(
      `/qr-print/products/${product.id}/customize?${params.toString()}`,
    );
  };

  return (
    <div className="qr-print-static-modal-backdrop">
      <section className="qr-print-static-modal">
        <button
          type="button"
          className="qr-print-static-modal-close"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <X size={20} />
        </button>

        <header className="qr-print-static-modal-header">
          <span>
            <PackageOpen size={15} />
            {t('qrPrint.static.eyebrow')}
          </span>

          <h2>
            {t('qrPrint.static.title')}
          </h2>

          <p>
            {t('qrPrint.static.description')}
          </p>
        </header>

        {error ? (
          <div className="qr-print-static-modal-error">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="qr-print-static-modal-loading">
            <Loader2 size={30} />

            <strong>
              {t('qrPrint.static.loading')}
            </strong>
          </div>
        ) : (
          <div className="qr-print-static-products">
            {sortedProducts.map(
              (product) => {
                const image =
                  getProductImage(
                    product,
                  );

                const material =
                  optionLabel(
                    product
                      ?.available_materials
                      ?.[0],
                  );

                const size =
                  optionLabel(
                    product
                      ?.available_sizes
                      ?.[0],
                  );

                return (
                  <button
                    key={product.id}
                    type="button"
                    className="qr-print-static-product-card"
                    onClick={() =>
                      openProduct(
                        product,
                      )
                    }
                  >
                    <div className="qr-print-static-product-media">
                      {image ? (
                        <img
                          src={image}
                          alt={
                            product.name ||
                            t('qrPrint.common.productFallback')
                          }
                        />
                      ) : (
                        <div className="qr-print-static-product-placeholder">
                          <ImageOff
                            size={30}
                          />

                          <span>
                            {t('qrPrint.static.noImage')}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="qr-print-static-product-body">
                      <span className="qr-print-static-product-type">
                        {product.product_type ||
                          t('qrPrint.common.productFallback')}
                      </span>

                      <strong>
                        {product.name}
                      </strong>

                      {product.description ? (
                        <p>
                          {
                            product.description
                          }
                        </p>
                      ) : null}

                      <div className="qr-print-static-product-meta">
                        {material ? (
                          <small>
                            {material}
                          </small>
                        ) : null}

                        {size ? (
                          <small>
                            {size}
                          </small>
                        ) : null}

                        <small>
                          {product.base_unit_price !=
                          null
                            ? t(
                                'qrPrint.common.fromPrice',
                                {
                                  price:
                                    formatMoney(
                                      product.base_unit_price,
                                    ),
                                },
                              )
                            : t('qrPrint.common.priceOnRequest')}
                        </small>
                      </div>

                      <span className="qr-print-static-product-action">
                        {t('qrPrint.static.customize')}
                        <ArrowRight
                          size={15}
                        />
                      </span>
                    </div>
                  </button>
                );
              },
            )}

            {!loading &&
            sortedProducts.length === 0 ? (
              <div className="qr-print-static-empty">
                <PackageOpen
                  size={34}
                />

                <strong>
                  {t('qrPrint.static.empty.title')}
                </strong>

                <span>
                  {t('qrPrint.static.empty.description')}
                </span>
              </div>
            ) : null}
          </div>
        )}

        <footer className="qr-print-static-modal-footer">
          <button
            type="button"
            onClick={onClose}
          >
            {t('qrPrint.static.skip')}
          </button>
        </footer>
      </section>
    </div>
  );
}